import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

import { countriesAsDropdown } from 'support/helpers/countries/countries';
import { PartyDTO } from 'support/types';
import { Alert } from 'components/Display/Alert/Alert';
import { BasePopup } from 'components/Display/BasePopup/BasePopup';
import { Button } from 'components/Form/Button';
import { Dropdown } from 'components/Form/Dropdown/Dropdown';
import { Input } from 'components/Form/Input/Input/Input';

enum PartyType {
  Billing = 'billing',
  Delivery = 'delivery',
}

type PartyFormData = {
  street: string;
  city: string;
  postal_code: string;
  country_code: string;
  state?: string;
  gln?: string;
  vatId?: string;
};

type PartyModalProps = {
  isOpen: boolean;
  mode: 'add' | 'edit';
  address?: PartyDTO;
  partyType: PartyType;
  onClose: () => void;
  onSave: (address: PartyDTO) => void;
  errorMessage?: string | null;
  isLoading?: boolean;
};

export function PartyModal({
  isOpen,
  mode,
  address,
  partyType,
  onClose,
  onSave,
  errorMessage,
  isLoading,
}: PartyModalProps) {
  const { t } = useTranslation();

  const initialGLN = address?.identifiers?.find((id) => id.domain === 'GS1')?.identifier;
  const initialVATID = address?.identifiers?.find((id) => id.domain === 'vatID')?.identifier;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
  } = useForm<PartyFormData>({
    defaultValues: getInitialValues(mode, address),
    resolver: yupResolver(createPartySchema(t, initialGLN, initialVATID)),
  });

  // Reset form when modal opens/closes or mode changes
  useEffect(() => {
    if (isOpen) {
      reset(getInitialValues(mode, address));
    }
  }, [isOpen, mode, address, reset]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSave = (data: PartyFormData) => {
    const identifiers = [];

    const initialGLN = address?.identifiers?.find((id) => id.domain === 'GS1');
    const initialVATID = address?.identifiers?.find((id) => id.domain === 'vatID');

    if (data.gln || initialGLN) {
      identifiers.push({
        domain: 'GS1' as const,
        identifier: data.gln || initialGLN?.identifier || '',
      });
    }

    if (partyType === PartyType.Billing) {
      if (data.vatId || initialVATID) {
        identifiers.push({
          domain: 'vatID' as const,
          identifier: data.vatId || initialVATID?.identifier || '',
        });
      }
    } else if (initialVATID) {
      identifiers.push(initialVATID);
    }

    const partyData: PartyDTO = {
      id: address?.id,
      street: data.street,
      city: data.city,
      state: data.state,
      postal_code: data.postal_code,
      country_code: data.country_code,
      identifiers,
    };

    onSave(partyData);
  };

  const showVatField =
    partyType === PartyType.Billing ||
    (partyType === PartyType.Delivery && !!address?.identifiers?.find((id) => id.domain === 'vatID'));

  const vatDisabled = partyType === PartyType.Delivery;

  return (
    <BasePopup open={isOpen} setOpen={handleClose} width="md">
      <BasePopup.Body>
        <h2 className="mb-6 text-lg font-semibold text-gray-900">{t('orderForms:checkout.addressModal.title')}</h2>

        <form onSubmit={handleSubmit(handleSave)} className="space-y-4">
          <Input
            label={t('orderForms:checkout.addressModal.fields.street')}
            errors={errors.street}
            required={true}
            {...register('street')}
          />

          <Input
            label={t('orderForms:checkout.addressModal.fields.city')}
            errors={errors.city}
            required={true}
            {...register('city')}
          />

          <Input
            label={t('orderForms:checkout.addressModal.fields.zipPostal')}
            errors={errors.postal_code}
            required={true}
            {...register('postal_code')}
          />

          <Input
            label={t('orderForms:checkout.addressModal.fields.state')}
            errors={errors.state}
            {...register('state')}
          />

          <Controller
            name="country_code"
            control={control}
            render={({ field }) => (
              <Dropdown
                id="country"
                label={t('orderForms:checkout.addressModal.fields.country')}
                options={countryOptions}
                errors={errors.country_code}
                value={field.value}
                onChange={field.onChange}
                required={true}
              />
            )}
          />

          <Input label={t('orderForms:checkout.addressModal.fields.gln')} errors={errors.gln} {...register('gln')} />

          {showVatField && (
            <Input
              label={t('orderForms:checkout.addressModal.fields.vatId')}
              errors={errors.vatId}
              disabled={vatDisabled}
              {...register('vatId')}
            />
          )}

          {errorMessage && (
            <Alert severity="error" className="mt-4">
              <p className="text-sm">{errorMessage}</p>
            </Alert>
          )}

          <div className="flex justify-end gap-3">
            <Button type="button" variant="secondary" onClick={handleClose} analyticsId="party-modal-cancel">
              {t('common:cancel')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              analyticsId="party-modal-save"
              disabled={isLoading}
              loading={isLoading}
            >
              {t('orderForms:checkout.addressModal.saveButton')}
            </Button>
          </div>
        </form>
      </BasePopup.Body>
    </BasePopup>
  );
}

const createPartySchema = (
  t: (key: string) => string,
  initialGLN: string | undefined,
  initialVATID: string | undefined,
) => {
  const validGln = () =>
    Yup.string().matches(/^\d{13}$/, {
      message: t('orderForms:checkout.addressModal.validation.glnMustBe13Digits'),
      excludeEmptyString: true,
    });

  return Yup.object().shape({
    street: Yup.string().required(t('common:table.errors.requiredField')),
    city: Yup.string().required(t('common:table.errors.requiredField')),
    postal_code: Yup.string().required(t('common:table.errors.requiredField')),
    country_code: Yup.string().required(t('common:table.errors.requiredField')),
    state: Yup.string().optional(),
    gln: initialGLN ? validGln().required(t('common:table.errors.requiredField')) : validGln().optional(),
    vatId: initialVATID ? Yup.string().required(t('common:table.errors.requiredField')) : Yup.string().optional(),
  });
};

const getInitialValues = (mode: 'add' | 'edit', address?: PartyDTO): PartyFormData => {
  if (mode === 'edit' && address) {
    const gln = address.identifiers?.find((id) => id.domain === 'GS1')?.identifier || '';
    const vatId = address.identifiers?.find((id) => id.domain === 'vatID')?.identifier || '';

    return {
      street: address.street || '',
      city: address.city || '',
      postal_code: address.postal_code || '',
      state: address.state || '',
      country_code: address.country_code || '',
      gln,
      vatId,
    };
  }

  return {
    street: '',
    city: '',
    postal_code: '',
    state: '',
    country_code: '',
    gln: '',
    vatId: '',
  };
};

const countryOptions = countriesAsDropdown();
